import { Material } from "@babylonjs/core/Materials/material";
import { MmdAsyncTextureLoader } from "./mmdAsyncTextureLoader";
import { ReferenceFileResolver } from "./referenceFileResolver";
import { TextureAlphaChecker } from "./textureAlphaChecker";
/**
 * Render method of MMD material
 *
 * The drawing behavior of MMD is not conducive to modern renderers like Babylon.js
 * That's why you need to decide which shading method is right for your use case
 */
export var MmdMaterialRenderMethod;
(function (MmdMaterialRenderMethod) {
    /**
     * Force depth write alpha blending with alpha evaluation
     *
     * This approach first determines via alpha evaluation if the meshes to be rendered are opaque,
     * and then only enables forceDepthWrite and performs alphaBlending on non-opaque meshes
     *
     * This approach is similar to mmd, but is more performance friendly and partially solves the draw order problem
     */
    MmdMaterialRenderMethod[MmdMaterialRenderMethod["DepthWriteAlphaBlendingWithEvaluation"] = 0] = "DepthWriteAlphaBlendingWithEvaluation";
    /**
     * Force depth write alpha blending
     *
     * Materials loaded this way will all have forceDepthWrite true and will alphaBlend true
     *
     * Since it does depth writing and alpha blending, the draw order becomes very important
     *
     * This approach gives you exactly the same results as mmd,
     * but it introduces a problem that mmd is known for: manually managing the draw order
     */
    MmdMaterialRenderMethod[MmdMaterialRenderMethod["DepthWriteAlphaBlending"] = 1] = "DepthWriteAlphaBlending";
    /**
     * Alpha evaluation
     *
     * This method uses an alpha evaluation to determine whether the mesh is best rendered as opaque, alphatest, or alphablend
     *
     * Since this method does not use forceDepthWrite, it can give different results than mmd but has better compatibility for several shader effects
     */
    MmdMaterialRenderMethod[MmdMaterialRenderMethod["AlphaEvaluation"] = 2] = "AlphaEvaluation";
})(MmdMaterialRenderMethod || (MmdMaterialRenderMethod = {}));
/**
 * Material builder base class for creating mmd model materials
 */
export class MaterialBuilderBase {
    /**
     * Render method of MMD standard material (default: DepthWriteAlphaBlendingWithEvaluation)
     */
    renderMethod = MmdMaterialRenderMethod.DepthWriteAlphaBlendingWithEvaluation;
    /**
     * Whether to force disable alpha evaluation (default: false)
     *
     * If true, load time alpha evaluation will be disabled
     *
     * For load time optimization, it is recommended to disable alpha evaluation feature and set the blending mode for the material manually
     */
    forceDisableAlphaEvaluation = false;
    /**
     * The threshold of material alpha to use transparency mode. (default: 195)
     *
     * lower value is more likely to use transparency mode. (0 - 255)
     */
    alphaThreshold = 195;
    /**
     * The threshold of transparency mode to use alpha blend. (default: 100)
     *
     * lower value is more likely to use alpha test mode. otherwise use alpha blend mode
     */
    alphaBlendThreshold = 100;
    /**
     * The canvas resolution to evaluate alpha (default: 512)
     *
     * Resolution of the render canvas used to evaluate alpha internally
     *
     * The higher the resolution, the higher the accuracy and the longer the load time
     */
    alphaEvaluationResolution = 512;
    /**
     * Whether to delete the texture buffer after loading (default: true)
     */
    deleteTextureBufferAfterLoad = true;
    /**
     * For parallel texture loading and caching duplicate texture requests
     *
     * we use a custom texture loader to load the textures
     */
    _textureLoader = new MmdAsyncTextureLoader();
    /**
     * Next starting alpha index for force depth write alpha blending rendering
     */
    nextStartingAlphaIndex = 1 << 16;
    /**
     * Alpha index increments per model for force depth write alpha blending rendering
     */
    alphaIndexIncrementsPerModel = 1 << 10;
    _materialConstructor;
    /**
     * pass the material constructor to the builder for creating the material
     * @param materialConstructor material constructor
     */
    constructor(materialConstructor) {
        this._materialConstructor = materialConstructor;
    }
    buildMaterials(uniqueId, materialsInfo, texturesInfo, imagePathTable, rootUrl, fileRootId, referenceFiles, referencedMeshes, meshes, scene, assetContainer, textureNameMap, logger, onTextureLoadProgress, onTextureLoadComplete) {
        if (this.renderMethod === MmdMaterialRenderMethod.DepthWriteAlphaBlendingWithEvaluation ||
            this.renderMethod === MmdMaterialRenderMethod.DepthWriteAlphaBlending) {
            this._setMeshesAlphaIndex(meshes);
        }
        // Block the marking of materials dirty until all materials are built.
        const oldBlockMaterialDirtyMechanism = scene.blockMaterialDirtyMechanism;
        scene._forceBlockMaterialDirtyMechanism(true);
        let textureAlphaChecker = null;
        const getTextureAlphaChecker = () => {
            if (textureAlphaChecker !== null)
                return textureAlphaChecker;
            return this.forceDisableAlphaEvaluation
                ? null
                : textureAlphaChecker = new TextureAlphaChecker(scene, this.alphaEvaluationResolution);
        };
        const referenceFileResolver = new ReferenceFileResolver(referenceFiles, rootUrl, fileRootId);
        const promises = [];
        const progressEvent = {
            lengthComputable: true,
            loaded: 0,
            total: materialsInfo.length * 3
        };
        const incrementProgress = () => {
            progressEvent.loaded += 1;
            onTextureLoadProgress?.(progressEvent);
        };
        const materials = [];
        for (let i = 0; i < materialsInfo.length; ++i) {
            const materialInfo = materialsInfo[i];
            scene._blockEntityCollection = !!assetContainer;
            const material = new this._materialConstructor(materialInfo.name, scene);
            material._parentContainer = assetContainer;
            scene._blockEntityCollection = false;
            assetContainer?.materials.push(material);
            {
                const singleMaterialPromises = [];
                const loadScalarPropertiesPromise = this.loadGeneralScalarProperties(material, materialInfo, referencedMeshes[i]);
                if (loadScalarPropertiesPromise !== undefined) {
                    singleMaterialPromises.push(loadScalarPropertiesPromise);
                }
                const loadDiffuseTexturePromise = this.loadDiffuseTexture(uniqueId, material, materialInfo, imagePathTable, (texturesInfo[materialInfo.textureIndex] ?? null), scene, assetContainer, rootUrl, referenceFileResolver, logger, incrementProgress);
                const createSetAlphaBlendModePromise = () => {
                    return this.setAlphaBlendMode(material, materialInfo, referencedMeshes[i], logger, getTextureAlphaChecker);
                };
                if (loadDiffuseTexturePromise !== undefined) {
                    const setAlphaBlendModePromise = loadDiffuseTexturePromise.then(createSetAlphaBlendModePromise);
                    singleMaterialPromises.push(setAlphaBlendModePromise);
                }
                else {
                    const setAlphaBlendModePromise = createSetAlphaBlendModePromise();
                    if (setAlphaBlendModePromise !== undefined) {
                        singleMaterialPromises.push(setAlphaBlendModePromise);
                    }
                }
                const loadSphereTexturePromise = this.loadSphereTexture(uniqueId, material, materialInfo, imagePathTable, (texturesInfo[materialInfo.sphereTextureIndex] ?? null), scene, assetContainer, rootUrl, referenceFileResolver, logger, incrementProgress);
                if (loadSphereTexturePromise !== undefined) {
                    singleMaterialPromises.push(loadSphereTexturePromise);
                }
                const loadToonTexturePromise = this.loadToonTexture(uniqueId, material, materialInfo, imagePathTable, (texturesInfo[materialInfo.toonTextureIndex] ?? null), scene, assetContainer, rootUrl, referenceFileResolver, logger, incrementProgress);
                if (loadToonTexturePromise !== undefined) {
                    singleMaterialPromises.push(loadToonTexturePromise);
                }
                const loadOutlineRenderingPropertiesPromise = this.loadOutlineRenderingProperties(material, materialInfo, logger);
                if (loadOutlineRenderingPropertiesPromise !== undefined) {
                    singleMaterialPromises.push(loadOutlineRenderingPropertiesPromise);
                }
                promises.push(...singleMaterialPromises);
                Promise.all(singleMaterialPromises).then(() => {
                    this.afterBuildSingleMaterial(material, i, // materialIndex
                    materialInfo, imagePathTable, texturesInfo, scene, rootUrl);
                });
            }
            materials.push(material);
        }
        this._textureLoader.loadModelTexturesEnd(uniqueId);
        const onModelTextureLoadedObservable = this._textureLoader.onModelTextureLoadedObservable.get(uniqueId);
        if (onModelTextureLoadedObservable !== undefined) {
            onModelTextureLoadedObservable.addOnce(() => {
                Promise.all(promises).then(() => {
                    textureAlphaChecker?.dispose();
                    // Restore the blocking of material dirty.
                    scene._forceBlockMaterialDirtyMechanism(oldBlockMaterialDirtyMechanism);
                    if (textureNameMap !== null)
                        this._buildTextureNameMap(materialsInfo, materials, imagePathTable, texturesInfo, textureNameMap);
                    onTextureLoadComplete?.();
                });
            });
        }
        else {
            Promise.all(promises).then(() => {
                textureAlphaChecker?.dispose();
                // Restore the blocking of material dirty.
                scene._forceBlockMaterialDirtyMechanism(oldBlockMaterialDirtyMechanism);
                if (textureNameMap !== null)
                    this._buildTextureNameMap(materialsInfo, materials, imagePathTable, texturesInfo, textureNameMap);
                onTextureLoadComplete?.();
            });
        }
        return materials;
    }
    _setMeshesAlphaIndex(meshes) {
        let alphaIndex = this.nextStartingAlphaIndex;
        for (let i = 0; i < meshes.length; ++i) {
            meshes[i].alphaIndex = alphaIndex;
            alphaIndex += 1;
        }
        this.nextStartingAlphaIndex += this.alphaIndexIncrementsPerModel;
    }
    /**
     * Evaluate diffuse texture transparency mode from the diffuse texture and meshes
     * @param diffuseTexture diffuse texture
     * @param evaluatedTransparency evaluated transparency
     * @param referencedMeshes meshes that use the diffuse texture
     * @param logger logger
     * @param getTextureAlphaChecker get texture alpha checker function
     * @returns transparency mode
     */
    async _evaluateDiffuseTextureTransparencyModeAsync(diffuseTexture, evaluatedTransparency, referencedMeshes, logger, getTextureAlphaChecker) {
        let transparencyMode = Number.MIN_SAFE_INTEGER;
        if (this.renderMethod === MmdMaterialRenderMethod.DepthWriteAlphaBlendingWithEvaluation) {
            let etIsNotOpaque = (evaluatedTransparency >> 4) & 0x03;
            if ((etIsNotOpaque ^ 0x03) === 0) { // 11: not evaluated
                etIsNotOpaque = -1;
            }
            if (etIsNotOpaque === -1) {
                transparencyMode = Material.MATERIAL_OPAQUE;
                const textureAlphaChecker = getTextureAlphaChecker();
                if (textureAlphaChecker !== null) {
                    for (let i = 0; i < referencedMeshes.length; ++i) {
                        const referencedMesh = referencedMeshes[i];
                        const isMeshOpaque = await textureAlphaChecker.hasFragmentsOnlyOpaqueOnGeometryAsync(diffuseTexture, referencedMesh?.mesh ?? referencedMesh, referencedMesh?.subMeshIndex !== undefined
                            ? referencedMesh.subMeshIndex
                            : null);
                        if (!isMeshOpaque) {
                            transparencyMode = Material.MATERIAL_ALPHABLEND;
                            break;
                        }
                    }
                }
            }
            else if (etIsNotOpaque === 0) { // 00: opaque
                transparencyMode = Material.MATERIAL_OPAQUE;
            }
            else {
                transparencyMode = Material.MATERIAL_ALPHABLEND;
            }
        }
        else if (this.renderMethod === MmdMaterialRenderMethod.AlphaEvaluation) {
            let etAlphaEvaluateResult = evaluatedTransparency & 0x0F;
            if ((etAlphaEvaluateResult ^ 0x0F) === 0) { // 1111: not evaluated
                etAlphaEvaluateResult = -1;
            }
            if (etAlphaEvaluateResult !== -1) {
                transparencyMode = etAlphaEvaluateResult;
            }
            else {
                const textureAlphaChecker = getTextureAlphaChecker();
                if (textureAlphaChecker !== null) {
                    for (let i = 0; i < referencedMeshes.length; ++i) {
                        const referencedMesh = referencedMeshes[i];
                        const newTransparencyMode = await textureAlphaChecker.hasTranslucentFragmentsOnGeometryAsync(diffuseTexture, referencedMesh?.mesh ?? referencedMesh, referencedMesh?.subMeshIndex !== undefined
                            ? referencedMesh.subMeshIndex
                            : null, this.alphaThreshold, this.alphaBlendThreshold);
                        if (transparencyMode < newTransparencyMode) {
                            transparencyMode = newTransparencyMode;
                        }
                    }
                }
            }
        }
        else {
            logger.warn(`Unknown shading method for evaluating transparency mode: ${this.renderMethod}`);
        }
        return transparencyMode !== Number.MIN_SAFE_INTEGER ? transparencyMode : null;
    }
    /**
     * Called after building a single material
     *
     * This method is called after the material and textures have been loaded
     * @param material Material
     * @param materialIndex Material index
     * @param materialInfo Material information
     * @param imagePathTable Image path table
     * @param texturesInfo Texture information
     * @param scene Scene
     * @param rootUrl Root url
     */
    afterBuildSingleMaterial = () => { };
}
